// billingChangeApi.js — client side of the staged-edit flow for Client Billing Review.
//
// Edits are NOT written live: submitChange() queues a PENDING change that the Billing
// Team approves (api/diBillingChange → api/shared/billingConfig/). The server returns
// 202 with { changeId, summary } on success, or 400/409 with { error, code } when a rule
// is violated — submitChange throws an Error carrying .code / .status for those.
//
// section / fieldKey / payload shapes (see api/shared/billingConfig/registry.js):
//   billing_group     fieldKey 'billing_group'          payload { parentCode }
//   billing_group     fieldKey 'append_client_name'     payload { value: bool }
//   client_standard   fieldKey (ignored)  op set|delete payload { service, value }   (delete: { service })
//   recurring         fieldKey (ignored)  op set|delete payload = recurring body (PascalCase:
//                       uuid, BillType, Level, Frequency, Population, Narratives[{lineOrder,narrative,amount}],
//                       BillAmount, LumpDisbursements, LumpWithParent, ApplicableThrough); delete: { uuid }
//   force_bill        fieldKey (ignored)                payload { service, enabled: bool }
//   misc              fieldKey techFee | freebieReturn | discountToggle | rollIndtaxIntoBustax | excludeClient
//                                                       payload { value: bool }
//   delivery_format   fieldKey ClientInvDeliveryFormat | ClientDeliveryFormat   payload { value: 0|1 }
//                       the value 1 (Email) request that makes invoice AND statement both Email also needs
//                       { creditEmail, billingEmail, statementEmail }, all filled in (once, not per format)
//
// Setting something back to its live value (turn off / delete / revert a proposal) is answered with
// status 'withdrawn': the server cancels the open request instead of queueing a new one.

// Tells the open Billing Approvals queue (same tab) that the pending list just changed.
export const BILLING_CHANGES_EVENT = 'di:billing-changes';
const announce = () => { try { window.dispatchEvent(new Event(BILLING_CHANGES_EVENT)); } catch { /* non-browser */ } };

async function readError(res) {
  const text = await res.text();
  let body = null;
  try { body = JSON.parse(text); } catch { /* plain text */ }
  const err = new Error((body && body.error) || text || `HTTP ${res.status}`);
  err.status = res.status;
  err.code = body && body.code;
  err.floor = body && body.floor;
  return err;
}

// In-flight tracking. The grid patches itself optimistically the moment an edit is sent; a refetch that
// was answered before ALL of those edits reached the server would put the older state back (the row
// "flashes" and jumps when several edits go out in quick succession). The page therefore ignores a
// refetch that overlapped any submit / withdraw — see mutationsInFlight / mutationStamp, used by the
// `reconcile` of Billing Review's useSectionData. The last edit to finish triggers a clean refetch.
let inFlight = 0;
let stamp = 0;
export const mutationsInFlight = () => inFlight;
export const mutationStamp = () => stamp;
async function tracked(run) {
  inFlight += 1; stamp += 1;
  try { return await run(); } finally { inFlight -= 1; stamp += 1; }
}
// The page's other optimistic writes (sign-offs, unmark, unsubmit) join the same tracking so a refetch that
// predates them can't flash the grid back either.
export const trackMutation = tracked;

export function submitChange({ period, contIndex, section, fieldKey, op = 'set', payload }) {
  return tracked(() => sendChange({ period, contIndex, section, fieldKey, op, payload }));
}

async function sendChange({ period, contIndex, section, fieldKey, op, payload }) {
  const res = await fetch(`/api/diBillingChange/${encodeURIComponent(period)}`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ contIndex, section, fieldKey, op, payload }),
  });
  if (!res.ok) throw await readError(res);
  announce();
  // { changeId, summary, section, fieldKey, status: 'pending' } — or status 'withdrawn' (changeId null,
  // withdrawn: [ids]) when the edit put the setting back to what's live, which cancels the open request.
  return res.json();
}

export function withdrawChange({ period, changeId }) {
  return tracked(() => sendWithdraw({ period, changeId }));
}

async function sendWithdraw({ period, changeId }) {
  const res = await fetch(`/api/diBillingChange/${encodeURIComponent(period)}/${encodeURIComponent(changeId)}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!res.ok) throw await readError(res);
  announce();
  return res.json();
}

// True when submitChange's answer means "your pending request was withdrawn", not "queued".
export const wasWithdrawn = out => !!out && out.status === 'withdrawn';

// Find a client's pending change for one setting (client.pending is attached by diBillingReview).
export function pendingFor(client, section, fieldKey) {
  return (client.pending || []).find(p => p.section === section && (fieldKey == null || p.fieldKey === fieldKey)) || null;
}

// ── optimistic helpers ───────────────────────────────────────────────────────
// The grid shows the "Pending approval" tag the moment an edit is sent, and takes it away the
// moment a withdraw is clicked — the server's answer (silentRefetch) then replaces our guess, and
// a failure restores the snapshot. These mirror api/shared/billingConfig/registry.js + overlay.js.

const norm = v => String(v ?? '').trim();
const lcn = v => norm(v).toLowerCase();

// Same canonical key the server stores (registry.canonicalFieldKey) so tags match pendingFor().
export function stagedKey(section, fieldKey, payload) {
  switch (section) {
    case 'client_standard': return `std:${norm(payload?.service).toUpperCase()}`;
    case 'recurring':       return `recurring:${lcn(payload?.uuid)}`;
    case 'force_bill':      return `force_bill:${norm(payload?.service).toUpperCase()}`;
    case 'narrative':       return `narrative:${lcn(payload?.uuid)}`;
    default:                return norm(fieldKey);
  }
}

// The open request for one setting, if any (what pendingFor finds, by canonical key).
export function pendingForKey(client, section, fieldKey, payload) {
  const key = stagedKey(section, fieldKey, payload);
  return (client.pending || []).find(p => p.section === section && p.fieldKey === key) || null;
}

// Would this edit put the setting back to what's live? Then the server withdraws the open request
// instead of queueing a new one (changes.revertsToLive). `pending.before` is the live value it saw.
export function revertsPending(pending, { section, op = 'set', payload }, email) {
  if (!pending) return false;
  // Only your OWN open request is taken back by a revert (the server enforces the same rule).
  if (!email || lcn(pending.requestedBy) !== lcn(email)) return false;
  const b = pending.before;
  const p = payload || {};
  if (op === 'delete') {
    if (section === 'client_standard') return !!b && b.value == null;
    if (section === 'recurring' || section === 'narrative') return pending.op === 'set' && !b;
    return false;
  }
  if (!b) return false;
  switch (section) {
    case 'misc':            return typeof p.value === 'boolean' && p.value === b.value;
    case 'billing_group':   return typeof p.value === 'boolean' ? p.value === b.value : lcn(p.parentCode) === lcn(b.parentCode);
    case 'force_bill':      return typeof p.enabled === 'boolean' && p.enabled === b.enabled;
    case 'delivery_format': return b.value != null && p.value === b.value;
    case 'client_standard': return b.value != null && p.value !== '' && p.value != null && Number(p.value) === Number(b.value);
    default:                return false;
  }
}

// client with an optimistic "Pending approval" entry for this edit (replacing any older one for the
// same setting). `changeId: null` marks it as not yet confirmed — the tag shows, Withdraw waits.
export function withPending(client, { section, fieldKey, op = 'set', payload, summary, email }) {
  const key = stagedKey(section, fieldKey, payload);
  const rest = (client.pending || []).filter(p => !(p.section === section && p.fieldKey === key));
  return {
    ...client,
    pending: [...rest, {
      changeId: null, contIndex: client.contIndex, section, fieldKey: key, op,
      summary: summary || 'Waiting for the Billing Team', requestedBy: email || '', requestedAt: new Date().toISOString(),
      before: null, optimistic: true,
    }],
  };
}

export function withoutPending(client, section, fieldKey, payload) {
  const key = stagedKey(section, fieldKey, payload);
  return { ...client, pending: (client.pending || []).filter(p => !(p.section === section && p.fieldKey === key)) };
}

// What the row looks like once `change` is withdrawn: the tag gone and the setting back to the live
// value the change recorded. Returns null when that can't be rebuilt locally (a recurring edit or a
// pending delete) — the caller then waits for the server's refetch instead of guessing.
export function withdrawnView(client, change) {
  const base = { ...client, pending: (client.pending || []).filter(p => p.changeId !== change.changeId) };
  const b = change.before;
  switch (change.section) {
    case 'misc':
      return b && typeof b.value === 'boolean' ? { ...base, [change.fieldKey]: b.value } : null;
    case 'delivery_format':
      if (!b || b.value === undefined) return null;
      // An Email request recorded the live addresses too: put them back with the format.
      return {
        ...base, [change.fieldKey]: b.value,
        ...(b.creditEmail !== undefined
          ? { ClientCreditEmail: b.creditEmail, ClientBillingEmail: b.billingEmail, ClientStatementEmail: b.statementEmail }
          : {}),
      };
    case 'billing_group': {
      if (!b) return null;
      const bg = base.billingGroup || {
        parentCode: base.clientCode, children: [base.clientCode], childCount: 0,
        isParent: false, isSelfOnly: true, violation: false, appendClientName: false,
      };
      if (change.fieldKey === 'append_client_name') {
        return typeof b.value === 'boolean' ? { ...base, billingGroup: { ...bg, appendClientName: b.value } } : null;
      }
      const parent = norm(b.parentCode) || base.clientCode;
      return { ...base, billingGroup: { ...bg, parentCode: parent, parentName: parent === base.clientCode ? '' : bg.parentName, isSelfOnly: !bg.isParent && parent === base.clientCode } };
    }
    case 'force_bill': {
      if (!b || typeof b.enabled !== 'boolean') return null;
      const serv = String(change.fieldKey).replace(/^force_bill:/, '');
      const rest = (base.forceBill || []).filter(f => f.service !== serv);
      return { ...base, forceBill: b.enabled ? [...rest, { service: serv }] : rest };
    }
    case 'client_standard': {
      if (!b || change.op === 'delete') return null;
      const serv = String(change.fieldKey).replace(/^std:/, '');
      const rest = (base.clientStandards || []).filter(s => s.service !== serv);
      return { ...base, clientStandards: b.value == null ? rest : [...rest, { service: serv, value: Number(b.value) }] };
    }
    case 'recurring': {
      if (b || change.op === 'delete') return null;                 // an edit/delete: can't rebuild the old bill here
      const uuid = String(change.fieldKey).replace(/^recurring:/, '');
      const same = r => r && lcn(r.uuid) === uuid;
      return { ...base, recurringMonthly: same(base.recurringMonthly) ? null : base.recurringMonthly,
               recurringQuarterly: same(base.recurringQuarterly) ? null : base.recurringQuarterly };
    }
    default:
      return null;
  }
}
